import { injectable } from 'inversify'

/**
 * Sends an unhandled client-side error to the server so a bug that only
 * reproduces on someone else's device lands in the same store the admin
 * error browser reads (`POST /client-errors` on the backend).
 *
 * Like LoggingGateway, this takes no constructor dependencies: the two
 * callers — the crash boundary and the global error listener — must work
 * even when the DI container is the thing that failed, so they `new` it
 * directly. It reads the API base from `import.meta.env`, a build-time
 * constant, for the same reason.
 *
 * Fire-and-forget and fully swallowed: a failure to report an error must
 * never itself surface an error.
 */
@injectable()
export class ErrorReportGateway {
  private readonly apiUrl: string = (import.meta.env?.VITE_API_URL as string | undefined) ?? 'http://localhost:4000'

  report(message: string, stack?: string): void {
    try {
      void fetch(`${this.apiUrl}/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, stack, url: window.location?.href }),
        keepalive: true,
      }).catch(() => {})
    } catch {
      // no-op — reporting must never throw
    }
  }
}
